import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const PLATFORMS = {
  all:         { label: 'All channels', icon: '⊞', color: '#fff', bg: '#534AB7' },
  amazon:      { label: 'Amazon',       icon: 'A',  color: '#fff', bg: '#E47911' },
  ebay:        { label: 'eBay',         icon: 'E',  color: '#fff', bg: '#0064D2' },
  shopify:     { label: 'Shopify',      icon: 'S',  color: '#fff', bg: '#96BF48' },
  bigcommerce: { label: 'BigCommerce',  icon: 'B',  color: '#fff', bg: '#34313F' },
};

const ORDER_STATUS_COLORS = {
  new:       { bg: '#EEF6FF', color: '#1A56DB', label: 'New' },
  pending:   { bg: '#FFF8E6', color: '#B45309', label: 'Pending' },
  shipped:   { bg: '#ECFDF5', color: '#065F46', label: 'Shipped' },
  in_transit:{ bg: '#EFF6FF', color: '#1D4ED8', label: 'In Transit' },
  delivered: { bg: '#ECFDF5', color: '#065F46', label: 'Delivered' },
  return:    { bg: '#FEF2F2', color: '#991B1B', label: 'Return' },
  cancelled: { bg: '#F3F4F6', color: '#6B7280', label: 'Cancelled' },
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [agents, setAgents] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({ agent_id: '', platform: '', status: '' });
  const [msgCounts, setMsgCounts] = useState({});
  const [orderCounts, setOrderCounts] = useState({});
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (['amazon','ebay','shopify','bigcommerce'].includes(tab)) params.platform = tab;
      const orderParams = {};
      if (['amazon','ebay','shopify','bigcommerce'].includes(tab)) orderParams.platform = tab;
      const statParams = ['amazon','ebay','shopify','bigcommerce'].includes(tab) ? { platform: tab } : {};
      const [msgRes, ordRes, statRes] = await Promise.all([
        api.get('/messages', { params }),
        api.get('/orders', { params: orderParams }),
        api.get('/messages/stats', { params: statParams }),
      ]);
      setMessages(msgRes.data.messages);
      setOrders(ordRes.data.orders);
      setStats(statRes.data);
      setLastRefreshed(new Date());
    } catch {}
  }, [tab]);

  // Initial load + reload when tab changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Agents + per-platform sidebar counts
  useEffect(() => {
    if (user?.role === 'admin') {
      api.get('/users').then(r => setAgents(r.data.filter(u => u.role === 'agent' && u.is_active)));
    }
    Promise.all(
      ['amazon','ebay','shopify','bigcommerce'].map(p =>
        Promise.all([
          api.get('/messages', { params: { platform: p } }),
          api.get('/orders', { params: { platform: p } }),
        ]).then(([m, o]) => ({ platform: p, msgs: m.data.messages?.length || 0, orders: o.data.orders?.length || 0 }))
      )
    ).then(results => {
      const mc = {}, oc = {};
      results.forEach(r => { mc[r.platform] = r.msgs; oc[r.platform] = r.orders; });
      setMsgCounts(mc); setOrderCounts(oc);
    });
  }, [user]);

  // Auto-refresh every 10 minutes
  useEffect(() => {
    const intervalId = setInterval(() => {
      loadData();
    }, 10 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [loadData]);

  const syncAll = async () => {
    setSyncing(true);
    try { await api.post('/sync/all'); await loadData(); }
    catch {} finally { setSyncing(false); }
  };

  const markRead = async (id) => {
    await api.put(`/messages/${id}/read`);
    setMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: 1 } : m));
  };

  const assignMsg = async (msgId, agentId) => {
    await api.put(`/messages/${msgId}/assign`, { agent_id: agentId });
    setAssignModal(null);
    loadData();
  };

  const bulkAssign = async () => {
    await api.post('/messages/bulk-assign', bulkForm);
    setBulkModal(false);
    setBulkForm({ agent_id: '', platform: '', status: '' });
    loadData();
  };

  const platformOrderUrl = (platform, orderId) => {
    if (platform === 'shopify') return `https://admin.shopify.com/store/81jx11-pm/orders/${orderId}`;
    if (platform === 'bigcommerce') return `https://store-9842s2iis2.mybigcommerce.com/manage/orders/${orderId}`;
    if (platform === 'ebay') return `https://www.ebay.com/mesh/ord/details?orderId=${orderId}`;
    if (platform === 'amazon') return `https://sellercentral.amazon.com/orders-v3/order/${orderId}`;
    return '#';
  };

  const PlatformBadge = ({ platform, size = 'sm' }) => {
    const p = PLATFORMS[platform] || PLATFORMS.all;
    const sz = size === 'lg' ? 36 : 26;
    return (
      <div style={{ width: sz, height: sz, borderRadius: '50%', background: p.bg, color: p.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size === 'lg' ? 15 : 11, fontWeight: 700, flexShrink: 0 }}>
        {p.icon}
      </div>
    );
  };

  const StatusBadge = ({ status }) => {
    const s2 = ORDER_STATUS_COLORS[status] || { bg: '#F3F4F6', color: '#6B7280', label: status };
    return <span style={{ background: s2.bg, color: s2.color, fontSize: 11, padding: '3px 9px', borderRadius: 20, fontWeight: 600 }}>{s2.label}</span>;
  };

  const currentPlatform = PLATFORMS[tab] || PLATFORMS.all;
  const unreadMessages = messages.filter(m => !m.is_read).length;

  return (
    <div style={s.root}>
      {/* Sidebar */}
      <div style={s.sidebar}>
        <div style={s.sidebarTop}>
          <div style={s.logo}>SB</div>
        </div>

        <div style={s.navSection}>
          {Object.entries(PLATFORMS).map(([key, p]) => {
            const count = (msgCounts[key] || 0) + (orderCounts[key] || 0);
            const isActive = tab === key;
            return (
              <div key={key} onClick={() => setTab(key)}
                style={{ ...s.navItem, background: isActive ? p.bg : 'transparent' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? '#fff' : '#9CA3AF', flex: 1 }}>
                  {p.label}
                </span>
                {key !== 'all' && count > 0 && (
                  <span style={{ background: isActive ? 'rgba(255,255,255,0.25)' : '#EF4444', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center' }}>
                    {count}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div style={s.sidebarBottom}>
          {lastRefreshed && (
            <span style={{ fontSize: '12px', color: '#888' }}>
              Last updated: {lastRefreshed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
            </span>
          )}
          {user?.role === 'admin' && <>
            <div style={s.navItem} onClick={() => navigate('/users')}>
              <span style={s.bottomIcon}>👥</span>
              <span style={s.bottomLabel}>Users</span>
            </div>
            <div style={s.navItem} onClick={() => navigate('/settings')}>
              <span style={s.bottomIcon}>⚙</span>
              <span style={s.bottomLabel}>Settings</span>
            </div>
            <div style={s.navItem} onClick={syncAll}>
              <span style={{ ...s.bottomIcon, color: syncing ? '#555' : '#7C74E8' }}>{syncing ? '⟳' : '↻'}</span>
              <span style={{ ...s.bottomLabel, color: syncing ? '#555' : '#7C74E8' }}>{syncing ? 'Syncing...' : 'Sync all'}</span>
            </div>
            <div style={s.navItem} onClick={() => setBulkModal(true)}>
              <span style={s.bottomIcon}>📋</span>
              <span style={s.bottomLabel}>Bulk assign</span>
            </div>
          </>}
          <div style={s.navItem} onClick={loadData}>
            <span style={s.bottomIcon}>⟳</span>
            <span style={s.bottomLabel}>Refresh</span>
          </div>
          <div style={s.navItem} onClick={logout}>
            <span style={{ ...s.bottomIcon, color: '#EF4444' }}>⏻</span>
            <span style={{ ...s.bottomLabel, color: '#EF4444' }}>Sign out</span>
          </div>
        </div>
      </div>

      {/* Main */}
      <div style={s.main}>
        <div style={s.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <PlatformBadge platform={tab} size="lg" />
            <div>
              <div style={s.headerTitle}>{currentPlatform.label}</div>
              <div style={s.headerSub}>{user?.name} · {user?.role}</div>
            </div>
          </div>
          <div style={s.statsRow}>
            {[
              { val: `$${Number(stats.revenueToday || 0).toLocaleString('en', { minimumFractionDigits: 2 })}`, label: 'Revenue today' },
              { val: stats.ordersToday || 0, label: 'Orders today' },
              { val: unreadMessages, label: 'Unread', highlight: unreadMessages > 0 },
              ...(stats.urgent > 0 ? [{ val: stats.urgent, label: 'Urgent', urgent: true }] : []),
            ].map((item, i, arr) => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={s.statItem}>
                  <div style={{ ...s.statVal, color: item.urgent ? '#DC2626' : item.highlight ? '#4338CA' : '#111827' }}>{item.val}</div>
                  <div style={s.statLabel}>{item.label}</div>
                </div>
                {i < arr.length - 1 && <div style={s.statDivider} />}
              </div>
            ))}
          </div>
        </div>

        <div style={s.content}>
          {/* Orders */}
          <div style={s.panel}>
            <div style={s.panelHeader}>
              <span style={s.panelTitle}>Orders</span>
              <span style={s.panelCount}>{orders.length}</span>
            </div>
            <div style={s.panelBody}>
              {orders.length === 0 && <div style={s.empty}>No orders</div>}
              {orders.map(o => (
                <div key={o.id} style={s.orderCard}>
                  <div style={s.orderTop}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <PlatformBadge platform={o.platform} />
                      <div>
                        <a href={platformOrderUrl(o.platform, o.platform_order_id)} target="_blank" rel="noreferrer" style={s.orderLink}>
                          #{o.platform_order_id?.slice(-8)} ↗
                        </a>
                        <div style={s.orderCustomer}>{o.customer_name || 'Unknown'}</div>
                      </div>
                    </div>
                    <StatusBadge status={o.status} />
                  </div>
                  <div style={s.orderProduct}>{o.product_name}</div>
                  <div style={s.orderMeta}>
                    <span>{o.customer_location}</span>
                    <span style={s.orderAmount}>${Number(o.amount || 0).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Messages */}
          <div style={s.panel}>
            <div style={s.panelHeader}>
              <span style={s.panelTitle}>Messages</span>
              <span style={s.panelCount}>{messages.length}</span>
              {unreadMessages > 0 && <span style={s.unreadBadge}>{unreadMessages} unread</span>}
            </div>
            <div style={s.panelBody}>
              {messages.length === 0 && <div style={s.empty}>No messages</div>}
              {messages.map(msg => (
                <div key={msg.id}
                  style={{ ...s.msgCard, ...(msg.is_read ? {} : s.msgUnread), ...(selectedMsg?.id === msg.id ? s.msgSelected : {}) }}
                  onClick={() => { setSelectedMsg(msg); markRead(msg.id); }}>
                  <div style={s.msgTop}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <PlatformBadge platform={msg.platform} />
                      <div>
                        <div style={s.msgFrom}>{msg.sender_name || msg.sender_id || 'Unknown'}</div>
                        <div style={s.msgTime}>{new Date(msg.received_at).toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      {!msg.is_read && <div style={s.unreadDot} />}
                      {msg.is_urgent && <span style={s.urgentTag}>Urgent</span>}
                    </div>
                  </div>
                  <div style={s.msgSubject}>{msg.subject}</div>
                  <div style={s.msgPreview}>{msg.preview}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    {msg.assigned_to_name ? <span style={s.assignedTag}>→ {msg.assigned_to_name}</span> : <span />}
                    {user?.role === 'admin' && (
                      <button style={s.assignBtn} onClick={e => { e.stopPropagation(); setAssignModal(msg); }}>
                        {msg.assigned_to_name ? 'Reassign' : 'Assign'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {assignModal && (
        <div style={s.overlay} onClick={() => setAssignModal(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Assign message</div>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>{assignModal.subject}</p>
            <select style={s.select} onChange={e => assignMsg(assignModal.id, e.target.value)} defaultValue="">
              <option value="">Unassign</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <button style={{ ...s.cancelBtn, marginTop: 12 }} onClick={() => setAssignModal(null)}>Cancel</button>
          </div>
        </div>
      )}

      {bulkModal && (
        <div style={s.overlay} onClick={() => setBulkModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Bulk assign messages</div>
            <label style={s.label}>Assign to agent</label>
            <select style={s.select} value={bulkForm.agent_id} onChange={e => setBulkForm({ ...bulkForm, agent_id: e.target.value })}>
              <option value="">Select agent</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <label style={s.label}>Filter by platform (optional)</label>
            <select style={s.select} value={bulkForm.platform} onChange={e => setBulkForm({ ...bulkForm, platform: e.target.value })}>
              <option value="">All platforms</option>
              {['amazon','ebay','shopify','bigcommerce'].map(p => <option key={p} value={p}>{PLATFORMS[p].label}</option>)}
            </select>
            <label style={s.label}>Filter by status (optional)</label>
            <select style={s.select} value={bulkForm.status} onChange={e => setBulkForm({ ...bulkForm, status: e.target.value })}>
              <option value="">All statuses</option>
              {['open','pending','resolved'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button style={s.primaryBtn} onClick={bulkAssign} disabled={!bulkForm.agent_id}>Assign</button>
              <button style={{ ...s.cancelBtn, marginTop: 0 }} onClick={() => setBulkModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  root: { display: 'flex', height: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif', background: '#F9FAFB', overflow: 'hidden' },
  sidebar: { width: 160, background: '#1a1a2e', display: 'flex', flexDirection: 'column', alignItems: 'stretch', padding: '12px 0', flexShrink: 0 },
  sidebarTop: { padding: '0 12px', marginBottom: 16 },
  logo: { width: 36, height: 36, borderRadius: 10, background: '#534AB7', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 },
  navSection: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' },
  navItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s' },
  navActive: {},
  sidebarBottom: { display: 'flex', flexDirection: 'column', gap: 1, padding: '8px 8px 0', borderTop: '1px solid #2a2a4a', marginTop: 8 },
  bottomIcon: { fontSize: 14, flexShrink: 0, width: 20, textAlign: 'center' },
  bottomLabel: { fontSize: 12, color: '#6B7280', flex: 1, marginLeft: 8 },
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px', background: '#fff', borderBottom: '1px solid #E5E7EB', flexShrink: 0 },
  headerTitle: { fontSize: 17, fontWeight: 600, color: '#111827' },
  headerSub: { fontSize: 11, color: '#9CA3AF', marginTop: 2 },
  statsRow: { display: 'flex', alignItems: 'center' },
  statItem: { padding: '0 18px', textAlign: 'right' },
  statVal: { fontSize: 19, fontWeight: 600, color: '#111827' },
  statLabel: { fontSize: 11, color: '#9CA3AF', marginTop: 1 },
  statDivider: { width: 1, height: 30, background: '#E5E7EB' },
  content: { display: 'grid', gridTemplateColumns: '1fr 1fr', flex: 1, overflow: 'hidden' },
  panel: { display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid #E5E7EB' },
  panelHeader: { display: 'flex', alignItems: 'center', gap: 8, padding: '11px 18px', background: '#fff', borderBottom: '1px solid #E5E7EB', flexShrink: 0 },
  panelTitle: { fontSize: 13, fontWeight: 600, color: '#374151' },
  panelCount: { background: '#F3F4F6', color: '#6B7280', fontSize: 11, padding: '2px 7px', borderRadius: 10, fontWeight: 500 },
  unreadBadge: { background: '#EEF2FF', color: '#4338CA', fontSize: 11, padding: '2px 7px', borderRadius: 10, fontWeight: 500, marginLeft: 'auto' },
  panelBody: { flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 },
  empty: { textAlign: 'center', color: '#D1D5DB', fontSize: 13, padding: 48 },
  orderCard: { background: '#fff', borderRadius: 10, padding: '13px 14px', border: '1px solid #E5E7EB' },
  orderTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  orderLink: { fontSize: 13, fontWeight: 600, color: '#4338CA', textDecoration: 'none', display: 'block' },
  orderCustomer: { fontSize: 11, color: '#9CA3AF', marginTop: 2 },
  orderProduct: { fontSize: 12, color: '#374151', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  orderMeta: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9CA3AF' },
  orderAmount: { fontWeight: 600, color: '#111827', fontSize: 12 },
  msgCard: { background: '#fff', borderRadius: 10, padding: '13px 14px', border: '1px solid #E5E7EB', cursor: 'pointer' },
  msgUnread: { borderLeft: '3px solid #534AB7' },
  msgSelected: { background: '#EEF2FF', borderColor: '#818CF8' },
  msgTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 },
  msgFrom: { fontSize: 13, fontWeight: 600, color: '#111827' },
  msgTime: { fontSize: 10, color: '#9CA3AF', marginTop: 2 },
  msgSubject: { fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  msgPreview: { fontSize: 11, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  unreadDot: { width: 8, height: 8, borderRadius: '50%', background: '#534AB7' },
  urgentTag: { background: '#FEF2F2', color: '#DC2626', fontSize: 10, padding: '2px 6px', borderRadius: 6, fontWeight: 600 },
  assignedTag: { fontSize: 11, color: '#9CA3AF' },
  assignBtn: { fontSize: 11, color: '#4338CA', background: 'transparent', border: '1px solid #C7D2FE', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#fff', borderRadius: 16, padding: 28, width: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' },
  modalTitle: { fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#111827' },
  label: { display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 6, marginTop: 14, fontWeight: 500 },
  select: { width: '100%', padding: '9px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', color: '#374151' },
  primaryBtn: { flex: 1, padding: '10px 0', background: '#4338CA', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  cancelBtn: { flex: 1, padding: '10px 0', background: '#F3F4F6', color: '#6B7280', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
};
